import type { Agent } from "@/types/aiToolkit";
import { getSkillById } from "@/data/agentSkills";
import { DEFAULT_CHAT_HANDOFF } from "@/lib/agentChatHandoff";

const CREATED = "2024-01-01";

function skillSchema(skillId: string): Record<string, unknown> | undefined {
  return getSkillById(skillId)?.outputSchema;
}

const commsBase = {
  tier: "agent" as const,
  access: "free" as const,
  category: "Communications",
  memoryPolicy: { type: "session" as const, includeContext: true },
  executionMode: "manual" as const,
  isTemplate: true,
  createdAt: CREATED,
  createdBy: "system" as const,
  actionPermissions: { chatHandoff: DEFAULT_CHAT_HANDOFF },
};

export const COMMUNICATION_DEFAULT_AGENTS: Agent[] = [
  {
    ...commsBase,
    id: "agent-context-sanitizer",
    name: "Context Sanitizer",
    description: "Separates business vs casual content from meeting transcripts.",
    icon: "Filter",
    color: "bg-teal-500",
    tags: ["meetings", "fireflies", "zoom", "workflow"],
    nodeType: "specialist",
    persona: {
      role: "Context Sanitizer",
      expertise: ["Meeting analysis", "Content classification"],
      communicationStyle: "Structured JSON output",
      systemPrompt:
        "Separate business-relevant discussion from casual chat in meeting transcripts. Output structured JSON with business and casual sections.",
    },
    capabilityModules: ["cap-summarization"],
    integrationScopes: ["fireflies", "zoom"],
    stepRole: "extract",
    attachedSkillIds: ["skill-meeting-separate-chat"],
    outputSchema: skillSchema("skill-meeting-separate-chat"),
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-deliverable-architect",
    name: "Deliverable Architect",
    description: "Extracts actionable deliverables and tasks from meetings.",
    icon: "ListChecks",
    color: "bg-blue-600",
    tags: ["meetings", "clickup", "fireflies", "zoom", "workflow"],
    nodeType: "specialist",
    persona: {
      role: "Deliverable Architect",
      expertise: ["Task extraction", "Project planning"],
      communicationStyle: "Structured deliverables list",
      systemPrompt:
        "Extract deliverables, owners, and due dates from meeting content. Return JSON with a deliverables array suitable for task creation.",
    },
    capabilityModules: ["cap-analysis", "cap-clickup-write"],
    integrationScopes: ["fireflies", "zoom", "clickup"],
    stepRole: "extract",
    executionMode: "both",
    allowedToolIds: ["clickup"],
    attachedSkillIds: ["skill-meeting-deliverables"],
    outputSchema: skillSchema("skill-meeting-deliverables"),
    preferredModel: "anthropic/claude-3.5-sonnet",
  },
  {
    ...commsBase,
    id: "agent-business-extractor",
    name: "Business Extractor",
    description: "Extracts key business decisions and action items.",
    icon: "Briefcase",
    color: "bg-indigo-500",
    tags: ["meetings", "fireflies", "zoom", "workflow"],
    nodeType: "specialist",
    persona: {
      role: "Business Extractor",
      expertise: ["Decision tracking", "Action items"],
      communicationStyle: "Concise structured output",
      systemPrompt:
        "Extract business decisions, commitments, and action items from meeting transcripts.",
    },
    capabilityModules: ["cap-analysis", "cap-summarization"],
    integrationScopes: ["fireflies", "zoom"],
    stepRole: "extract",
    attachedSkillIds: ["skill-meeting-business-extract"],
    outputSchema: skillSchema("skill-meeting-business-extract"),
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-comms-meeting-exec-summary",
    name: "Meeting Executive Summary",
    description: "BLUF executive summary for leadership from meeting transcripts.",
    icon: "FileText",
    color: "bg-slate-600",
    tags: ["meetings", "fireflies", "zoom"],
    nodeType: "specialist",
    persona: {
      role: "Executive Summarizer",
      expertise: ["Executive communication", "Meeting synthesis"],
      communicationStyle: "Concise BLUF bullets",
      systemPrompt:
        "Write a bottom-line-up-front executive summary. Lead with the most important outcome in 3-5 bullets.",
    },
    capabilityModules: ["cap-summarization"],
    integrationScopes: ["fireflies", "zoom"],
    attachedSkillIds: ["skill-meeting-executive-summary"],
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-comms-meeting-participants",
    name: "Meeting Participant Mapper",
    description: "Maps who said what and identifies follow-up owners.",
    icon: "Target",
    color: "bg-cyan-600",
    tags: ["meetings", "fireflies", "zoom"],
    nodeType: "specialist",
    persona: {
      role: "Participant Mapper",
      expertise: ["Stakeholder mapping", "Accountability"],
      communicationStyle: "Structured participant breakdown",
      systemPrompt:
        "List participants, their roles if mentioned, key contributions, and suggested follow-up owners.",
    },
    capabilityModules: ["cap-analysis"],
    integrationScopes: ["fireflies", "zoom"],
    attachedSkillIds: ["skill-meeting-participants"],
    preferredModel: "google/gemini-2.5-flash",
  },
  {
    ...commsBase,
    id: "agent-comms-gmail-spam",
    name: "Gmail Spam Classifier",
    description: "Classify email as spam or legitimate with confidence score.",
    icon: "Shield",
    color: "bg-red-500",
    tags: ["gmail", "spam", "classification"],
    nodeType: "validator",
    persona: {
      role: "Spam Classifier",
      expertise: ["Email triage", "Phishing detection"],
      communicationStyle: "Structured JSON with confidence",
      systemPrompt:
        "Classify email as spam or legitimate. Support replies, billing, account notifications, and vendor mail for active services (including mailbox providers like Anytime Mailbox) are NOT spam unless clearly phishing. Only mark spam: true when confidence is at least 0.85. If uncertain, return spam: false. Cite specific signals in reason.",
    },
    capabilityModules: ["cap-analysis", "cap-gmail-write"],
    integrationScopes: ["gmail"],
    allowedToolIds: ["gmail"],
    attachedSkillIds: ["skill-gmail-spam-classify"],
    outputSchema: skillSchema("skill-gmail-spam-classify"),
    preferredModel: "google/gemini-2.5-flash",
  },
  {
    ...commsBase,
    id: "agent-comms-gmail-reply",
    name: "Gmail Reply Drafter",
    description: "Drafts a professional reply to an email with tone and confidence.",
    icon: "Reply",
    color: "bg-blue-500",
    tags: ["gmail", "reply", "draft"],
    nodeType: "specialist",
    persona: {
      role: "Email Reply Drafter",
      expertise: ["Professional communication", "Email etiquette"],
      communicationStyle: "Structured JSON with draft body",
      systemPrompt:
        "Draft a concise, professional reply to the email. Match the sender's tone when appropriate.",
    },
    capabilityModules: ["cap-writing", "cap-gmail-write"],
    integrationScopes: ["gmail"],
    allowedToolIds: ["gmail"],
    attachedSkillIds: ["skill-gmail-reply-draft"],
    outputSchema: skillSchema("skill-gmail-reply-draft"),
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-comms-gmail-triage",
    name: "Gmail Triage (composite)",
    description: "Layered sequence: spam classify, then reply draft for legitimate mail.",
    icon: "Layers",
    color: "bg-violet-500",
    tags: ["gmail", "triage", "composite"],
    nodeType: "orchestrator",
    persona: {
      role: "Gmail Triage Orchestrator",
      expertise: ["Email triage", "Spam filtering", "Reply drafting"],
      communicationStyle: "Sequential agent pipeline",
      systemPrompt: "Run spam classification first; only draft replies for non-spam email.",
    },
    capabilityModules: ["cap-analysis", "cap-writing", "cap-gmail-write"],
    integrationScopes: ["gmail"],
    allowedToolIds: ["gmail"],
    composedAgentIds: ["agent-comms-gmail-spam", "agent-comms-gmail-reply"],
    preferredModel: "google/gemini-2.5-flash",
  },
  {
    ...commsBase,
    id: "agent-comms-gmail-importance",
    name: "Gmail Importance Scanner",
    description: "Flags important emails and surfaces upcoming events.",
    icon: "Sparkles",
    color: "bg-amber-500",
    tags: ["gmail", "importance"],
    nodeType: "specialist",
    persona: {
      role: "Importance Scanner",
      expertise: ["Email prioritization", "Calendar awareness"],
      communicationStyle: "Structured importance assessment",
      systemPrompt:
        "Assess whether this email is important. Summarize why and list any dates or events mentioned.",
    },
    capabilityModules: ["cap-analysis"],
    integrationScopes: ["gmail"],
    attachedSkillIds: ["skill-gmail-importance"],
    preferredModel: "google/gemini-2.5-flash",
  },
  {
    ...commsBase,
    id: "agent-comms-gmail-analyst",
    name: "Gmail Analyst (raw)",
    description: "General email triage — summary, action items, urgency (no skill pack).",
    icon: "Mail",
    color: "bg-orange-500",
    tags: ["gmail", "general"],
    nodeType: "specialist",
    persona: {
      role: "Gmail Analyst",
      expertise: ["Email analysis", "Action extraction"],
      communicationStyle: "Clear markdown summary",
      systemPrompt: `Analyze the email and respond in Markdown:
- **Summary** (2-3 sentences)
- **Key entities** (people, dates, amounts)
- **Action items** with owners if identifiable
- **Urgency** (low / medium / high) and why`,
    },
    capabilityModules: ["cap-summarization", "cap-analysis"],
    integrationScopes: ["gmail"],
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-comms-zoom-summary",
    name: "Zoom Meeting Summary",
    description: "Executive summary for Zoom meeting recordings and transcripts.",
    icon: "Video",
    color: "bg-blue-500",
    tags: ["zoom", "meetings"],
    nodeType: "specialist",
    persona: {
      role: "Zoom Meeting Summarizer",
      expertise: ["Meeting synthesis", "Action items"],
      communicationStyle: "BLUF executive summary",
      systemPrompt:
        "Summarize the Zoom meeting with BLUF bullets, key decisions, and action items.",
    },
    capabilityModules: ["cap-summarization"],
    integrationScopes: ["zoom"],
    attachedSkillIds: ["skill-meeting-executive-summary"],
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-comms-telegram-analyst",
    name: "Telegram Analyst",
    description: "Summarize Telegram chats — key points, action items, urgency.",
    icon: "MessageCircle",
    color: "bg-sky-500",
    tags: ["telegram", "analysis"],
    nodeType: "specialist",
    persona: {
      role: "Telegram Analyst",
      expertise: ["Chat analysis", "Action extraction"],
      communicationStyle: "Clear markdown summary",
      systemPrompt: `Analyze the Telegram message thread and respond in Markdown:
- **Summary** (2-3 sentences)
- **Key entities** (people, dates, links)
- **Action items** with owners if identifiable
- **Urgency** (low / medium / high) and why`,
    },
    capabilityModules: ["cap-summarization", "cap-analysis", "cap-telegram-read"],
    integrationScopes: ["telegram"],
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-comms-telegram-reply",
    name: "Telegram Reply Drafter",
    description: "Draft a reply for Telegram with JSON output suitable for sending.",
    icon: "Send",
    color: "bg-sky-600",
    tags: ["telegram", "reply"],
    nodeType: "specialist",
    persona: {
      role: "Telegram Reply Drafter",
      expertise: ["Messaging", "Tone matching"],
      communicationStyle: "JSON with reply field",
      systemPrompt:
        'Draft a concise Telegram reply. Return JSON: { "reply": "message text", "tone": "professional|casual", "confidence": 0-1 }',
    },
    capabilityModules: ["cap-writing", "cap-telegram-write", "cap-telegram-read"],
    integrationScopes: ["telegram"],
    allowedToolIds: ["telegram"],
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-comms-telegram-deliverable",
    name: "Telegram Deliverable Architect",
    description: "Extract deliverables from Telegram for ClickUp (tasks, docs, comments).",
    icon: "ListChecks",
    color: "bg-sky-700",
    tags: ["telegram", "clickup"],
    nodeType: "specialist",
    persona: {
      role: "Deliverable Architect",
      expertise: ["Task extraction", "ClickUp routing"],
      communicationStyle: "Structured deliverables JSON",
      systemPrompt: `Extract deliverables from Telegram content. Return JSON:
{
  "detectedProject": { "name": "...", "confidence": 0-1, "reason": "..." },
  "deliverables": [{
    "title": "...",
    "description": "...",
    "owner": "...",
    "dueDate": "YYYY-MM-DD optional",
    "destinationType": "task|doc_page|task_comment"
  }]
}`,
    },
    capabilityModules: ["cap-analysis", "cap-clickup-write", "cap-telegram-read"],
    integrationScopes: ["telegram", "clickup"],
    executionMode: "both",
    allowedToolIds: ["clickup"],
    preferredModel: "anthropic/claude-3.5-sonnet",
  },
  {
    ...commsBase,
    id: "agent-comms-ghl-conversation-analyst",
    name: "GHL Conversation Analyst",
    description: "Summarize GoHighLevel conversation threads — key points, reply suggestions, urgency.",
    icon: "MessageCircle",
    color: "bg-emerald-500",
    tags: ["gohighlevel", "conversations", "analysis"],
    nodeType: "specialist",
    persona: {
      role: "GoHighLevel Conversation Analyst",
      expertise: ["CRM messaging", "Reply drafting"],
      communicationStyle: "Clear markdown summary",
      systemPrompt: `Analyze the GoHighLevel conversation thread and respond in Markdown:
- **Summary** (2-3 sentences)
- **Contact context** (name, channel)
- **Suggested reply** (draft message)
- **Urgency** (low / medium / high) and why`,
    },
    capabilityModules: ["cap-summarization", "cap-analysis"],
    integrationScopes: ["gohighlevel"],
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-comms-ghl-contact-insights",
    name: "GHL Contact Insights",
    description: "Analyze GoHighLevel contacts — tags, history, and follow-up recommendations.",
    icon: "Users",
    color: "bg-emerald-600",
    tags: ["gohighlevel", "contacts", "crm"],
    nodeType: "specialist",
    persona: {
      role: "GoHighLevel Contact Analyst",
      expertise: ["Contact profiling", "CRM follow-up"],
      communicationStyle: "Structured contact brief",
      systemPrompt: `Analyze the GoHighLevel contact and respond in Markdown:
- **Profile summary**
- **Tags & segments**
- **Recommended follow-up actions**
- **Priority** (low / medium / high)`,
    },
    capabilityModules: ["cap-summarization", "cap-analysis"],
    integrationScopes: ["gohighlevel"],
    preferredModel: "openai/gpt-4o-mini",
  },
  {
    ...commsBase,
    id: "agent-comms-ghl-pipeline-review",
    name: "GHL Pipeline Review",
    description: "Review GoHighLevel opportunities — stage analysis, deal health, next steps.",
    icon: "TrendingUp",
    color: "bg-emerald-700",
    tags: ["gohighlevel", "opportunities", "pipeline"],
    nodeType: "specialist",
    persona: {
      role: "GoHighLevel Pipeline Analyst",
      expertise: ["Deal review", "Pipeline forecasting"],
      communicationStyle: "Executive pipeline brief",
      systemPrompt: `Review the GoHighLevel opportunity and respond in Markdown:
- **Deal summary**
- **Stage assessment**
- **Risk factors**
- **Recommended next steps**`,
    },
    capabilityModules: ["cap-analysis"],
    integrationScopes: ["gohighlevel"],
    preferredModel: "openai/gpt-4o-mini",
  },
];

export function getCommunicationAgentsForScope(scope: string): Agent[] {
  return COMMUNICATION_DEFAULT_AGENTS.filter(
    (a) => a.integrationScopes?.includes(scope) || a.integrationScopes?.includes("cross")
  );
}

export function agentMatchesIntegrationScope(
  scopes: string[] | undefined,
  integrationScope: string
): boolean {
  if (!scopes?.length) return false;
  return scopes.includes(integrationScope) || scopes.includes("cross");
}
